import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";

import { PennText } from "@/components/ui";

import { beginLogin } from "@penn/web-common";

// Platform-issuer sign-in (AUTH_MODE=oidc). The Configurator hosts no sign-in
// form of its own in this mode — /login redirects straight to the platform
// identity issuer via beginLogin(), which runs its own challenge and redirects
// back to /auth/callback (CallbackPage) with an auth code. Cross-app and
// unregistered-user rejections are not pre-checked here; they surface
// post-callback from svc-02's session exchange (see CallbackPage).
//
// /login only auto-starts when the navigation carried intent (location.state
// .from, set by the app's own "sign in to do X" redirects, or an explicit
// state.autoStart from the NavBar Sign in button). A no-intent arrival renders
// a passive Sign in button instead: a sibling tab landing here after a cross-
// tab logout must never auto-authorize against an SSO cookie that is being
// destroyed (see App.tsx's cross-tab handler).
//
// One cross-origin arrival also carries intent: `?intent=signin`, appended by
// the platform identity service when it lands a freshly verified sign-up here
// with its SSO cookie already set. history.state cannot cross origins, so the
// query string is the only channel. Nothing on the logout path emits it, and
// the issuer resumes the session silently, so the user sees no second prompt.
export const OidcLoginPage = () => {
  const [error, setError] = useState<string | null>(null);
  const [redirecting, setRedirecting] = useState(false);
  const started = useRef(false);
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as {
    from?: { pathname?: string; search?: string };
    autoStart?: boolean;
    returnTo?: string;
  } | null;
  const signInIntent = new URLSearchParams(location.search).get("intent") === "signin";
  const hasIntent = !!state?.from || state?.autoStart === true || signInIntent;
  // `from` carries { pathname, search }; the search matters because a deep
  // link's parameters are the whole point of returning to it.
  const returnTo = state?.from?.pathname
    ? `${state.from.pathname}${state.from.search ?? ""}`
    : state?.returnTo;

  useEffect(() => {
    if (!hasIntent) return;
    // Consume the intent before leaving, so Back from the issuer lands on the
    // passive button instead of re-redirecting. Only the returnTo survives.
    const search = new URLSearchParams(location.search);
    if (signInIntent) search.delete("intent");
    const query = search.toString();
    navigate(
      { pathname: location.pathname, search: query ? `?${query}` : "", hash: location.hash },
      { replace: true, state: returnTo ? { returnTo } : null },
    );
    if (started.current) return;
    started.current = true;
    setRedirecting(true);
    beginLogin(returnTo).catch(() => {
      setError("Could not start sign-in. Please try again.");
    });
  }, [hasIntent, signInIntent, returnTo, location.pathname, location.search, location.hash, navigate]);

  // A bfcache restore does not remount, so drop the "Redirecting…" state here.
  useEffect(() => {
    const onPageShow = (e: PageTransitionEvent) => {
      if (!e.persisted) return;
      started.current = false;
      setRedirecting(false);
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="w-full max-w-lg text-center">
        {error ? (
          <div
            role="alert"
            className="rounded-xl border border-error/40 bg-default-surface px-3 py-2 text-[14px] leading-[21px] text-error-text"
          >
            {error}
          </div>
        ) : hasIntent || redirecting ? (
          <PennText as="p" size="base" align="center" color="on-navy-secondary">
            Redirecting you to sign in…
          </PennText>
        ) : (
          <>
            <PennText as="h1" size="2xl" align="center" color="on-navy" font="semibold" className="mb-4">
              Sign in to continue
            </PennText>
            <button
              className="px-5 py-2 rounded-xl text-sm font-semibold bg-penn-500 hover:bg-penn-700 text-white shadow-soft cursor-pointer"
              onClick={() => {
                setError(null);
                void beginLogin(returnTo).catch(() => {
                  setError("Could not start sign-in. Please try again.");
                });
              }}
            >
              Sign in
            </button>
          </>
        )}
      </div>
    </div>
  );
};
